import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ordersApi }        from '../../api/orders.js';
import { leadsApi }         from '../../api/leads.js';
import { followUpCallsApi } from '../../api/followUpCalls.js';

const isToday = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr), now = new Date();
  return d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
};

export default function TLDashboard({ user, onNavigate }) {
  const navigate = useNavigate();
  const [orders,    setOrders]    = useState([]);
  const [leadCount, setLeadCount] = useState(0);
  const [followUps, setFollowUps] = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [ordersRes, leadsRes, followRes] = await Promise.all([
          ordersApi.getAll({ limit: 50, sortBy: 'created_at', sortOrder: 'DESC' }),
          leadsApi.getAll({ limit: 1 }),
          followUpCallsApi.getAll({ limit: 20 }),
        ]);
        const orderList  = ordersRes.data ?? ordersRes;
        const followList = followRes.data  ?? followRes;
        setOrders(Array.isArray(orderList) ? orderList : []);
        setLeadCount(leadsRes.count ?? (Array.isArray(leadsRes.data) ? leadsRes.data.length : 0));
        setFollowUps(Array.isArray(followList) ? followList : []);
      } catch (err) {
        console.error('TLDashboard load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const todayOrders     = orders.filter(o => isToday(o.created_at));
  const completedOrders = orders.filter(o => o.status === 'delivered');
  const pendingFollowUps = followUps.filter(f => !f.is_completed);
  const overdueFollowUps = followUps.filter(f => {
    if (!f.scheduled_at || f.is_completed) return false;
    return new Date(f.scheduled_at) < new Date();
  });

  const pendingFUDisplay = followUps.filter(f => !f.is_completed).slice(0, 3).map(f => ({
    customer: (f.Lead || f.lead)?.name || `Lead #${f.lead_id}`,
    salesman: (f.User || f.user)?.name || `User #${f.user_id}`,
    date:     f.scheduled_at ? new Date(f.scheduled_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—',
    status:   overdueFollowUps.includes(f) ? 'Overdue' : 'Pending',
    type:     overdueFollowUps.includes(f) ? 'danger' : 'warning',
  }));

  return (
    <div className="page-body">
      {/* Profile banner */}
      <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:20,background:'var(--card)',padding:'14px 20px',borderRadius:10,border:'1px solid var(--border)' }}>
        <div className="av" style={{ width:44,height:44,fontSize:16,background:'var(--primary)',color:'#fff' }}>{user?.name?.charAt(0)||'V'}</div>
        <div>
          <div style={{ fontWeight:700,fontSize:15 }}>{user?.name||'Team Leader'}</div>
          <div style={{ fontSize:12,color:'var(--text-muted)' }}>Team Leader</div>
        </div>
        <div style={{ marginLeft:'auto' }}>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/place-order')}
            style={{ display:'flex',alignItems:'center',gap:6 }}>
            📦 Place Order
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="stats-grid" style={{ gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))' }}>
        {[
          ['Total Customers',  loading ? '…' : String(leadCount),              '',       'All leads'],
          ['Orders Today',     loading ? '…' : String(todayOrders.length),     'blue',   'Today'],
          ['Pending Follow-ups', loading ? '…' : String(pendingFollowUps.length), 'orange', 'Need attention'],
          ['Overdue',          loading ? '…' : String(overdueFollowUps.length), 'red',    'Past due date'],
          ['Completed Orders', loading ? '…' : String(completedOrders.length), 'green',  'Delivered'],
          ['Total Orders',     loading ? '…' : String(orders.length),          'purple', 'Last 50 fetched'],
        ].map(([l,v,c,ch]) => (
          <div key={l} className="stat-card">
            <div className="label">{l}</div>
            <div className={`value ${c}`}>{v}</div>
            <div className="change">{ch}</div>
          </div>
        ))}
      </div>

      <div className="dashboard-grid">
        {/* Recent orders */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Recent Orders (Today)</span>
            <button className="btn btn-outline btn-sm" onClick={() => onNavigate('orders')}>View All</button>
          </div>
          <div className="card-body" style={{ padding:0 }}>
            {loading ? (
              <div style={{ padding:'20px',textAlign:'center',color:'var(--text-muted)',fontSize:13 }}>Loading…</div>
            ) : todayOrders.length === 0 ? (
              <div style={{ padding:'20px',textAlign:'center',color:'var(--text-muted)',fontSize:13 }}>No orders placed today</div>
            ) : todayOrders.slice(0,5).map((o,i) => {
              const customer = (o.Lead || o.lead)?.name || `Lead #${o.lead_id}`;
              return (
                <div key={i} style={{ display:'flex',alignItems:'center',gap:12,padding:'12px 20px',borderBottom:'1px solid #f1f5f9' }}>
                  <div className="av">{customer.charAt(0)}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600,fontSize:13 }}>{customer}</div>
                    <div style={{ fontSize:11,color:'var(--text-muted)' }}>#{o.id}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontWeight:700,fontSize:13 }}>₹{Number(o.total_amount||0).toLocaleString('en-IN')}</div>
                    <span className={`badge badge-${o.status==='delivered'?'success':'warning'}`} style={{ fontSize:10 }}>{o.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Live map placeholder */}
        <div className="card">
          <div className="card-header"><span className="card-title">Live Map View</span></div>
          <div className="card-body">
            <div className="map-placeholder" style={{ height:180 }}>
              <svg viewBox="0 0 300 180" style={{ position:'absolute',inset:0,width:'100%',height:'100%',opacity:.4 }}>
                <path d="M0 90 Q75 60 150 90 T300 90" fill="none" stroke="#2563eb" strokeWidth="2"/>
              </svg>
              <div style={{ position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',color:'rgba(255,255,255,.5)',fontSize:13 }}>
                Map integration coming soon
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Follow-ups */}
      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-header">
          <span className="card-title">Pending Follow-ups</span>
          <button className="btn btn-outline btn-sm" onClick={() => onNavigate('visits')}>View All</button>
        </div>
        <div className="card-body" style={{ padding:0 }}>
          {loading ? (
            <div style={{ padding:'20px',textAlign:'center',color:'var(--text-muted)',fontSize:13 }}>Loading…</div>
          ) : pendingFUDisplay.length === 0 ? (
            <div style={{ padding:'20px',textAlign:'center',color:'var(--text-muted)',fontSize:13 }}>No pending follow-ups</div>
          ) : pendingFUDisplay.map((item,i) => (
            <div key={i} style={{ display:'flex',alignItems:'center',gap:14,padding:'12px 20px',borderBottom:'1px solid #f1f5f9' }}>
              <div className="av">{item.customer.charAt(0)}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:600,fontSize:13 }}>{item.customer}</div>
                <div style={{ fontSize:11,color:'var(--text-muted)' }}>{item.salesman}</div>
              </div>
              <div style={{ fontSize:12,color:'var(--text-muted)' }}>{item.date}</div>
              <span className={`badge badge-${item.type}`}>{item.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
